import { createFileRoute } from "@tanstack/react-router";
import { SpokePage, buildSpokeHead, type SpokePageConfig } from "@/components/SpokePage";

const CONFIG: SpokePageConfig = {
  slug: "urgent-care-web-design-colorado",
  metaTitle: "Urgent Care Web Design in Colorado | Websites That Book Jobs",
  metaDescription:
    "Custom urgent care web design across Colorado — fast, mobile-first, conversion-tested sites built to turn local searches into booked work. Owner-led. Call (719) 419-3935.",
  breadcrumbs: [
    { label: "Home", to: "/" },
    { label: "Medical practice marketing", to: "/medical-marketing-colorado" },
  ],
  currentLabel: "Urgent Care Web Design Colorado",
  eyebrow: "Urgent Care Web Design — Colorado",
  h1: (
    <>
      Urgent Care <span className="text-brand-gold">Web Design</span> in Colorado
    </>
  ),
  heroDescription:
    "Hand-coded urgent care websites that load in under 2 seconds, convert mobile searches, and rank from day one.",
  intro:
    "Urgent Care websites lose the lead in the first 4 seconds on a phone. We hand-code fast, mobile-first urgent care sites with sticky CTAs, dedicated service pages, and conversion-tested intake flows. No bloated templates, no page builders — just a fast custom site that turns local searches into booked work.",
  definition: {
    term: "Urgent Care web design in Colorado",
    body: "Urgent Care web design is the practice of building conversion-focused, mobile-first websites for urgent care businesses — with dedicated service pages, clear CTAs, fast load times, and proper local SEO structure baked in from day one.",
  },
  whatWeDo: [
    { title: "Hand-coded for speed", body: "Sub-2-second mobile load — urgent care is a now-search." },
    { title: "Real-time wait times & check-in", body: "Live wait-time widget and online check-in integration with Solv, DocuTAP, or your platform." },
    { title: "Service pages per visit type", body: "Sick visit, injury, X-ray, lab, occupational, sports physical — each ranks independently." },
    { title: "Service area + hours", body: "Hours of operation, locations, and accepted insurance surfaced above the fold." },
    { title: "Pediatric & adult flows", body: "Distinct content for parents searching pediatric urgent care." },
    { title: "Schema and SEO at launch", body: "MedicalBusiness, Service, FAQPage, Review schema baked in." },
  ],
  whyThisMarket: [
    { heading: "Wait time wins the visit", body: "Live wait times + online check-in beat the urgent care across the street every time." },
    { heading: "Visit-type pages capture long-tail", body: "\"Walk-in X-ray\", \"sports physical near me\", \"drug test near me\" are high-intent specific queries." },
    { heading: "Insurance clarity reduces no-shows", body: "Above-the-fold accepted-plan list cuts confusion and front-desk calls." },
  ],
  relatedLinks: [
    { to: "/medical-marketing-colorado", label: "Medical practice marketing hub" },
    { to: "/colorado-springs-web-design", label: "How our web design process works" },
    { to: "/web-design-pricing", label: "Web design pricing" },
    { to: "/colorado-springs", label: "Colorado Springs digital marketing agency" },
    { to: "/web-design-denver-colorado", label: "Denver web design" },
    { to: "/case-studies", label: "Colorado case studies" },
    { to: "/web-design", label: "Our web design service (all industries)" },
    { to: "/pricing", label: "Web design pricing" },
  ],
  faqs: [
    { q: "How much does a urgent care website cost in Colorado?", a: "Website packages start at $1,850 one-time. All include one year of hosting and a built-in traffic dashboard. Extra pages are $85 each. Compare all packages or build a plan on our pricing page." },
    { q: "How long does it take to build a urgent care website?", a: "Typical timeline is 4–6 weeks for a 12–20 page urgent care site with full service and service-area pages. Larger multi-location urgent care businesses can take 8–10 weeks." },
    { q: "Will the new site rank from day one?", a: "Existing rankings stay or improve because we build proper 301 redirects from every old URL, keep your domain, and ship with full schema and on-page SEO from launch — not as a phase 2 afterthought." },
    { q: "Do you only serve Colorado Springs urgent care businesses?", a: "No — we build urgent care sites for businesses across all of Colorado: Denver, Fort Collins, Pueblo, Castle Rock, Parker, Boulder, and the Front Range." },
  ],
  serviceName: "Urgent Care Web Design in Colorado",
  serviceDescription:
    "Hand-coded, mobile-first websites for urgent care businesses across Colorado. Fast, conversion-focused, and SEO-ready from launch.",
  areaServed: "Colorado",
};

export const Route = createFileRoute("/urgent-care-web-design-colorado")({
  head: () => buildSpokeHead(CONFIG),
  component: () => <SpokePage config={CONFIG} />,
});
